<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid }">
    <div class="block mb-6">
      <h4 class="title is-1">Neuigkeiten verfassen</h4>
    </div>
    <div class="columns">
      <div class="column is-8">
        <div class="columns is-align-items-center mb-0">
          <div class="column">
            <h4 class="title is-3 pb-3">Inhalt</h4>
          </div>
          <div class="column is-narrow">
            <section class="b-tooltips">
              <b-tooltip :label="visibleLabel">
                <b-button style="border: none">
                  <b-icon
                    :type="newsCopy.visible ? 'is-success' : 'is-danger'"
                    icon="circle"
                  />
                </b-button>
              </b-tooltip>
              <b-tooltip
                :label="newsCopy.visible ? 'Unpublish Post' : 'Publish Post'"
              >
                <b-button
                  @click="onPublishBtnClick"
                  type="is-light"
                  :icon-left="
                    newsCopy.visible ? 'cloud-download-alt' : 'cloud-upload-alt'
                  "
                />
              </b-tooltip>
              <b-tooltip v-if="newsCopy.id" label="Delete post">
                <b-button
                  @click="confirmDeleteNews(newsCopy.id)"
                  type="is-light"
                  icon-left="trash-alt"
                />
              </b-tooltip>
            </section>
          </div>
        </div>
        <div class="block">
          <div class="box">
            <div class="block">
              <BInputWithValidation
                rules="required|max:255"
                type="text"
                label="Titel"
                v-model="newsCopy.title"
              />
            </div>
            <div class="block">
              <BInputWithValidation
                rules="required"
                type="hidden"
                v-model="newsCopy.text"
              />
              <b-field label="Text">
                <vue-editor
                  id="main-text-editor"
                  v-model="newsCopy.text"
                  :editor-toolbar="toolbar"
                />
              </b-field>
            </div>
            <div class="block">
              <BInputWithValidation
                type="hidden"
                v-model="newsCopy.sidebox_text"
              />
              <b-field label="Seitenbox">
                <template #label>
                  <b-checkbox
                    v-model="newsCopy.sidebox_enabled"
                    @input="toggleSidebox"
                  >
                    Seitenbox
                  </b-checkbox>
                </template>
                <vue-editor
                  id="side-text-editor"
                  v-model="newsCopy.sidebox_text"
                  :editor-toolbar="toolbar"
                />
              </b-field>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-4">
        <div class="block">
          <h4 class="title is-3">Einstellungen</h4>
        </div>
        <div class="block" style="height: 100%">
          <div class="box" style="height: calc(100% - 60px)">
            <div class="block">
              <b-field label="Datum">
                <b-datepicker
                  v-model="createdAt"
                  placeholder="Select date..."
                  icon-right="calendar-day"
                  trap-focus
                >
                </b-datepicker>
              </b-field>
            </div>
            <div class="block">
              <b-field label="Kategorie">
                <b-dropdown
                  :mobile-modal="false"
                  :close-on-click="false"
                  :can-close="false"
                  expanded
                  scrollable
                  max-height="275px"
                  class="category-dropdown"
                >
                  <template #trigger="{ active }">
                    <b-button
                      type="is-outline-light"
                      :icon-right="active ? 'angle-up' : 'angle-down'"
                      expanded
                      class="is-justify-content-space-between"
                    >
                      <news-category-tag
                        v-if="newsCopy.category_id"
                        :data="theCategory(newsCopy.category_id)"
                      />
                      <span v-else class="has-text-grey">Select category</span>
                    </b-button>
                  </template>

                  <b-dropdown-item
                    v-for="category in categories"
                    :key="category.id"
                    class="is-flex is-align-items-center"
                  >
                    <b-radio
                      name="newsCategory"
                      :native-value="category.id"
                      v-model="newsCopy.category_id"
                      @input="handleCategoryChange"
                    >
                      <news-category-tag :data="category" />
                    </b-radio>

                    <b-button
                      icon-left="pencil-alt"
                      @click="openCategoryModal(theCategory(category.id))"
                      style="border: none"
                    />
                  </b-dropdown-item>

                  <b-dropdown-item style="padding-right: 1rem">
                    <b-button
                      type="is-light"
                      expanded
                      @click="openCategoryModal({ name: '', color: '#f5f5f5' })"
                    >
                      Neue Kategorie erstellen
                    </b-button>
                  </b-dropdown-item>
                </b-dropdown>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="block is-flex is-justify-content-end">
      <b-button
        type="is-gold"
        :disabled="invalid"
        @click="handleSubmit(emitNewsUpdate)"
      >
        Speichern
      </b-button>
    </div>
    <b-modal :active.sync="isCategoryModalActive" has-modal-card>
      <template #default="props">
        <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid }">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Kategorie</p>
              <b-button
                v-if="category.id"
                type="is-light-outline"
                icon-left="trash-alt"
                @click="confirmDeleteCategory"
              />
            </header>
            <div class="modal-card-body">
              <div class="block">
                <BInputWithValidation
                  label="Name"
                  rules="required|max:30"
                  type="text"
                  v-model="category.name"
                />
              </div>
              <div class="columns">
                <div class="column">
                  <div class="block">
                    <b-field label="Color">
                      <color-picker
                        v-model="selectedColor"
                        @input="(color) => (category.color = color.hex)"
                      />
                    </b-field>
                  </div>
                </div>
                <div class="column">
                  <div class="block">
                    <b-field label="Preview">
                      <news-category-tag :data="category" />
                    </b-field>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-card-foot is-flex is-justify-content-end">
              <b-button @click="props.close">Schließen</b-button>
              <b-button
                type="is-gold"
                :disabled="invalid"
                @click="handleSubmit(submitCategory)"
                >Speichern</b-button
              >
            </div>
          </div>
        </ValidationObserver>
      </template>
    </b-modal>
    <div v-if="error">
      <b-message type="is-danger">
        <span v-html="error" />
      </b-message>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueEditor } from 'vue2-editor';
import { Sketch } from 'vue-color';
import BInputWithValidation from '@/components/inputs/BInputWithValidation.vue';
import NewsCategoryTag from '@/components/editNews/NewsCategoryTag.vue';
import helper from '@/functions/helper';

export default {
  name: 'NewsEditor',
  components: {
    BInputWithValidation,
    VueEditor,
    ColorPicker: Sketch,
    NewsCategoryTag,
  },
  props: {
    news: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      newsCopy: { ...this.news },
      category: {
        name: '',
        color: '#f5f5f5',
      },
      selectedColor: {},
      isCategoryModalActive: false,
      toolbar: [
        ['clean'],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
      ],
    };
  },
  computed: {
    ...mapGetters({
      error: 'news/error',
      categories: 'newsCategory/data',
      theCategory: 'newsCategory/dataById',
    }),
    visibleLabel() {
      if (!this.newsCopy.id) {
        return this.newsCopy.visible ? 'Saved as News' : 'Saved as Draft';
      }

      return this.newsCopy.visible ? 'Published' : 'Draft';
    },
    createdAt: {
      get() {
        return new Date(this.newsCopy.created_at);
      },
      set(val) {
        this.newsCopy.created_at = helper.dateToTime(val);
      },
    },
  },
  watch: {
    news: {
      deep: true,
      handler(newVal) {
        this.newsCopy = { ...newVal };
      },
    },
  },
  methods: {
    emitNewsUpdate() {
      const payload = { ...this.newsCopy };
      // eslint-disable-next-line max-len
      // payload.sidebox_text = payload.sidebox_text ? payload.sidebox_text.replaceAll('target="_blank"', 'target="_self"') : payload.sidebox_text;
      // eslint-disable-next-line max-len
      // payload.text = payload.text ? payload.text.replaceAll('target="_blank"', 'target="_self"') : payload.text;
      this.$emit('submitted', payload);
    },
    handleCategoryChange() {
      if (!this.newsCopy.id) return;
      this.emitNewsUpdate();
    },
    toggleSidebox() {
      if (!this.newsCopy.id) return;
      this.emitNewsUpdate();
    },
    toggleNewsVisible() {
      this.newsCopy.visible = !this.newsCopy.visible;
    },
    onPublishBtnClick() {
      if (!this.newsCopy.id) {
        this.toggleNewsVisible();
        return;
      }

      this.confirmUnpublish();
    },
    confirmUnpublish() {
      if (this.newsCopy.visible) {
        this.$buefy.dialog.confirm({
          title: 'Neuigkeiten sperren',
          message: `Sind Sie sicher, dass Sie das Neuigkeiten <b>${this.newsCopy.title}</b> deaktivieren möchten?`,
          confirmText: 'Neuigkeiten deaktivieren',
          cancelText: 'Abbrechen',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => this.toggleNewsVisible(),
        });
      } else {
        this.toggleNewsVisible();
        this.emitNewsUpdate();
      }
    },
    deleteNews(id) {
      this.$store.dispatch('news/DELETE', { id });
    },
    confirmDeleteNews(newsId) {
      this.$buefy.dialog.confirm({
        title: 'Neuigkeiten löschen',
        // eslint-disable-next-line max-len
        message: `Sind Sie sicher, dass Sie das Neuigkeiten <strong>${this.newsCopy.title} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.</strong>`,
        confirmText: 'Neuigkeiten löschen',
        cancelText: 'Abbrechen',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteNews(newsId),
      });
    },
    openCategoryModal(category) {
      this.category = { ...category };
      this.isCategoryModalActive = true;
    },
    submitCategory() {
      const operation = this.category.id ? 'UPDATE' : 'CREATE';
      this.$store.dispatch(`newsCategory/${operation}`, this.category);

      // Todo after used for create operation
      if (!this.category.id) {
        this.resetCategoryModel();
      }
    },
    confirmDeleteCategory() {
      this.$buefy.dialog.confirm({
        title: 'Neuigkeiten kategorie löschen',
        // eslint-disable-next-line max-len
        message: 'Sind Sie sicher, die ausgewählte Neuigkeiten kategorie löschen möchten?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteCategory(),
      });
    },
    deleteCategory() {
      this.$store.dispatch('newsCategory/DELETE', { id: this.category.id });

      if (this.category.id === this.newsCopy.category_id) {
        this.newsCopy.category_id = null;
        this.emitNewsUpdate();
      }

      this.resetCategoryModel();
    },
    resetCategoryModel() {
      // Close modal
      this.isCategoryModalActive = false;

      // Reset model
      this.category = {};
      this.selectedColor = {};
    },
  },
};
</script>

<style>
#main-text-editor .ql-editor,
#side-text-editor .ql-editor {
  height: 200px;
}

.category-dropdown a.dropdown-item:hover {
  background-color: unset;
}
</style>
